import { useEffect, useMemo } from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getPaginationState, getRootPathString } from 'store/paginationPreservationSlice/selectors'
import {
  setPage as setPagePreserveState,
  setSortModel as setSortModelPreserveState,
} from 'store/paginationPreservationSlice'
import { Pagination } from './paginatedQuery'
import { useLocation } from 'react-router-dom'
import { GridCallbackDetails, GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import { Ordering } from './utils'
import { DataGridProps } from '@ketch-com/deck'

type UsePaginatedQueryPreservedPageArgs = {
  queryKey: string
  params: any
  totalPages: number
}

const usePagePreservation = ({ queryKey, params, totalPages }: UsePaginatedQueryPreservedPageArgs) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { pagination: reduxPaginationState } = useAppSelector(getPaginationState)
  const reduxRootPathString = useAppSelector(getRootPathString)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reduxPaginationStateKey = useMemo(() => JSON.stringify([queryKey, { ...params }]), [params])
  const reduxPage = reduxPaginationState?.[reduxPaginationStateKey] || 0

  useEffect(() => {
    dispatch(setPagePreserveState({ key: reduxPaginationStateKey, value: reduxPage }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxPaginationStateKey])

  useEffect(() => {
    if (reduxPage >= 0 && totalPages > 0 && reduxPage > totalPages - 1) {
      dispatch(setPagePreserveState({ key: reduxPaginationStateKey, value: totalPages - 1 }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxPage, totalPages])

  useEffect(() => {
    const hasRootPathChanged = async () => {
      const rootPathString = [location.pathname.split('/')?.[1], location.pathname.split('/')?.[2]].join('/')
      if (reduxRootPathString !== rootPathString) {
        dispatch(setPagePreserveState({ key: reduxPaginationStateKey, value: 0 }))
      }
    }
    hasRootPathChanged()
  }, [location, reduxRootPathString, dispatch, reduxPaginationStateKey])
}

export interface Sorting {
  dataGridProps: Pick<
    DataGridProps,
    'sortingMode' | 'sortingOrder' | 'sortModel' | 'onSortModelChange' | 'initialState'
  >
}

// Add ordering to the query parameters
interface SortableQueryParams {
  ordering?: Ordering
  [key: string]: any
}

/**
 * Converts a MUI GridSortModel to the API expected Ordering format
 */
const convertSortModelToOrdering = (sortModel: GridSortModel): Ordering | undefined => {
  if (!sortModel.length) return undefined

  const ordering: Ordering = {}
  sortModel.forEach(({ field, sort }) => {
    if (sort === 'asc' || sort === 'desc') {
      ordering[field] = sort
    }
  })

  return Object.keys(ordering).length ? ordering : undefined
}

export function createUsePaginatedQueryPreservedPage<
  QueryFnData,
  QueryFnParams extends SortableQueryParams,
  QuerySelectorData,
>({
  queryKey,
  queryFn,
  select,
  enabled = true,
}: {
  queryKey: string
  queryFn: (params: QueryFnParams) => Promise<QueryFnData>
  select: (res?: QueryFnData) => QuerySelectorData
  enabled?: boolean
}) {
  return function useCustomPaginatedQueryPreservedPage({
    params,
    itemsPerPage = 20,
    ...queryOptions
  }: { params: QueryFnParams; itemsPerPage?: number } & Omit<
    UseQueryOptions<QueryFnData, unknown>,
    'queryKey' | 'queryFn' | 'select' | 'keepPreviousData'
  >) {
    const dispatch = useAppDispatch()
    const { pagination: reduxPaginationState, sorting: reduxSortingState } = useAppSelector(getPaginationState)
    const reduxPaginationStateKey = useMemo(() => JSON.stringify([queryKey, { ...params }]), [params])
    const reduxPage = reduxPaginationState?.[reduxPaginationStateKey] || 0
    const reduxSortModel = reduxSortingState?.[reduxPaginationStateKey] || []

    // Prepare params for the query, including pagination and sorting
    const paginatedParams = {
      ...params,
      start: reduxPage * itemsPerPage,
      limit: itemsPerPage,
    } as QueryFnParams

    // Add ordering param if we have sort information
    if (reduxSortModel.length > 0) {
      paginatedParams.ordering = convertSortModelToOrdering(reduxSortModel)
    }

    const { data, isFetching, ...rest } = useQuery([queryKey, paginatedParams], () => queryFn(paginatedParams), {
      enabled,
      keepPreviousData: true,
      ...(queryOptions as any),
      refetchInterval: 0,
    })

    const totalResults = +((data as any)?.data?.total_results || (data as any)?.data?.totalResults || 0)
    const totalPages = Math.ceil(totalResults / itemsPerPage)

    usePagePreservation({ queryKey, params, totalPages })

    const pagination: Pagination = {
      isFirst: reduxPage === 0,
      isLast: reduxPage === totalPages - 1,
      page: reduxPage,
      totalPages,
      totalResults,
      rowsPerPage: itemsPerPage,
      onPageChange: nextPage => {
        dispatch(setPagePreserveState({ key: reduxPaginationStateKey, value: nextPage }))
      },
      isPending: isFetching,
      dataGridProps: {
        pagination: true,
        paginationMode: 'server',
        paginationModel: {
          page: reduxPage,
          pageSize: itemsPerPage,
        },
        onPaginationModelChange: (model: GridPaginationModel, _: GridCallbackDetails) => {
          dispatch(setPagePreserveState({ key: reduxPaginationStateKey, value: model.page }))
        },
        pageSizeOptions: [itemsPerPage],
        rowCount: totalResults,
        hideFooter: totalPages < 2,
      },
    }

    const sorting: Sorting = {
      dataGridProps: {
        sortingMode: 'server',
        sortModel: reduxSortModel,
        sortingOrder: ['asc', 'desc', null],
        initialState: {
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        },
        onSortModelChange: (newSortModel: GridSortModel) => {
          dispatch(setSortModelPreserveState({ key: reduxPaginationStateKey, value: newSortModel }))
          // When sorting changes, reset to first page
          dispatch(setPagePreserveState({ key: reduxPaginationStateKey, value: 0 }))
        },
      },
    }

    return {
      ...rest,
      isFetching,
      pagination,
      sorting,
      data: useMemo(() => select(data), [data]),
    }
  }
}
